import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { CheckCircle, ArrowCounterClockwise } from "@phosphor-icons/react";
import api from "../../../lib/api";
import { apiError, btnGhost, btnPrimary, fmtDateTimeWIB, inp } from "../util";

export const STATUS = { DRAFT: ["Draf", "bg-neutral-100 text-neutral-600"], SUBMITTED: ["Terkirim", "bg-sky-50 text-sky-800"], RETURNED: ["Dikembalikan", "bg-rose-50 text-rose-700"], APPROVED: ["Disetujui", "bg-sage-dark text-white"] };
export const JCHECK = { SESUAI: ["Sesuai", "text-emerald-700"], SELISIH: ["Selisih", "text-rose-700"], PERLU_DITINJAU: ["Perlu Ditinjau", "text-orange-600"], BELUM_DIPERIKSA: ["Belum Diperiksa", "text-amber-700"], TIDAK_TERSEDIA: ["Tidak Tersedia", "text-neutral-500"] };
export const LEVEL = { GREEN: ["Bagus", "bg-emerald-600"], ORANGE: ["Cukup Baik", "bg-orange-500"], RED: ["Perlu Improvement", "bg-rose-600"], NA: ["Tidak ada chat", "bg-neutral-400"] };
export const CHANNELS = [["internal", "Internal WA/IG"], ["shopee", "Shopee"], ["tokopedia", "Tokopedia"]];
export const uid = () => Math.random().toString(36).slice(2, 10) + Date.now().toString(36);

export function Pill({ status, testid }) {
    const [l, c] = STATUS[status] || [status, "bg-neutral-100"];
    return <span data-testid={testid} className={`inline-block px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-widest ${c}`}>{l}</span>;
}

export function JStatus({ s, testid }) {
    const [l, c] = JCHECK[s] || JCHECK.BELUM_DIPERIKSA;
    return <span data-testid={testid} className={`text-xs font-medium ${c}`}>{l}</span>;
}

export function Rate({ value, lvl, testid }) {
    const [l, c] = LEVEL[lvl] || LEVEL.NA;
    return (
        <span className="inline-flex items-center gap-1.5" data-testid={testid}>
            <span className="tabular-nums font-medium">{value === null || value === undefined ? "—" : `${value}%`}</span>
            <span className={`text-[10px] text-white px-1.5 py-0.5 rounded-sm ${c}`}>{l}</span>
        </span>
    );
}

export function Card({ title, children, testid, right }) {
    return (
        <section className="bg-white border border-mist rounded-sm p-4" data-testid={testid}>
            {title && <div className="flex items-center justify-between gap-2 mb-3"><h3 className="text-sm font-semibold text-ink">{title}</h3>{right}</div>}
            {children}
        </section>
    );
}

export function Row({ label, value, bold, testid }) {
    return <div className={`flex justify-between gap-3 py-1 text-sm ${bold ? "font-semibold border-t border-mist mt-1 pt-2" : ""}`} data-testid={testid}><span className="text-neutral-500">{label}</span><span className="tabular-nums text-right whitespace-nowrap">{value}</span></div>;
}

export function useLoad(url, params) {
    const [data, setData] = useState(null);
    const key = JSON.stringify(params || {});
    const seq = useRef(0);
    const load = useCallback(async () => {
        const n = ++seq.current;
        try {
            const res = (await api.get(url, { params: JSON.parse(key) })).data;
            if (n === seq.current) setData(res);
        } catch (e) {
            if (n === seq.current) toast.error(await apiError(e));
        }
    }, [url, key]);
    useEffect(() => { load(); }, [load]);
    return [data, load, setData];
}

export async function call(fn, ok) {
    try {
        const r = await fn();
        if (ok) toast.success(ok);
        return r.data;
    } catch (e) {
        toast.error(await apiError(e));
        return null;
    }
}

export function OwnerActions({ doc, base, onDone, approveBody }) {
    const [reason, setReason] = useState("");
    if (!doc?.id) return null;
    if (doc.status === "APPROVED") {
        return (
            <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="owner-approved">
                <span>Disetujui {fmtDateTimeWIB(doc.approval?.approved_at)} oleh {doc.approval?.approved_by}{doc.approval?.override_reason ? ` · alasan: ${doc.approval.override_reason}` : ""}</span>
                <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Alasan membuka ulang" className={`${inp} max-w-xs`} data-testid="reopen-reason" />
                <button type="button" disabled={!reason.trim()} className={btnGhost} data-testid="reopen-btn" onClick={async () => { const r = await call(() => api.post(`${base}/${doc.id}/reopen`, { reason }), "Laporan dibuka ulang"); if (r) onDone(r); }}><ArrowCounterClockwise size={14} /> Buka ulang</button>
            </div>
        );
    }
    if (doc.status !== "SUBMITTED") return null;
    return (
        <button type="button" className={btnPrimary} data-testid="approve-btn"
            onClick={async () => {
                const body = approveBody ? approveBody() : {};
                if (body === null || !window.confirm("Setujui dan kunci laporan ini?")) return;
                const r = await call(() => api.post(`${base}/${doc.id}/approve`, body), "Laporan disetujui");
                if (r) onDone(r);
            }}><CheckCircle size={14} /> Setujui</button>
    );
}

export function Revisions({ list }) {
    if (!list?.length) return null;
    return (
        <details className="text-xs text-neutral-600" data-testid="revisions">
            <summary className="cursor-pointer uppercase tracking-widest text-neutral-500">Riwayat revisi ({list.length})</summary>
            {list.map((r, i) => (
                <div key={i} className="py-1 border-b border-mist/60">
                    {fmtDateTimeWIB(r.at)} · {r.kind.replace("_", " ")} · {r.by}{r.reason ? ` · “${r.reason}”` : ""}
                    {r.changes?.length > 0 && <div className="text-neutral-400">{r.changes.map((c) => `${c.field}: ${typeof c.old === "object" ? "…" : c.old} → ${typeof c.new === "object" ? "…" : c.new}`).join(" · ")}</div>}
                </div>
            ))}
        </details>
    );
}
